import React from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { Button } from '@mobi/component-library/Common/V2/Buttons'
import { debounce } from '@mobi/utils/functions/debounce'
import { queryKeys } from '@core/Data/ReactQuery/constants'
import { getCopiedBetsFeed } from '@core/Areas/FollowMe/helpers/api'
import { CopiedFeedPageSize } from '@core/Areas/FollowMe/constants'

export const useCopiedBetsData = () => {
  const { data, isLoading, refetch, isRefetching, fetchNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: queryKeys.followMeSharedBetsFeedBase('copied'),

      queryFn: ({ pageParam }: { pageParam: number | null }) => getCopiedBetsFeed({ pageParam }),

      initialPageParam: null,

      getNextPageParam: lastPage => {
        if (!lastPage || lastPage.length < CopiedFeedPageSize) return undefined
        return lastPage[lastPage.length - 1]?.postId ?? undefined
      },

      staleTime: 1_000 * 60 * 2, // 2min
      gcTime: 1_000 * 60 * 5, // 5min
      refetchOnMount: 'always',
      refetchOnWindowFocus: false,
    })

  const isEmptyState = data?.pages?.[0]?.length === 0
  const isFinalPage = data && data.pages[data.pages.length - 1].length < CopiedFeedPageSize

  return {
    data,
    isLoading,
    refetch,
    isRefetching,
    LoadMoreButton: () =>
      isFinalPage || isEmptyState ? null : (
        <LoadMoreButton onClick={fetchNextPage} isFetching={isFetchingNextPage} />
      ),
  }
}

// ===============
// Local Component
// ===============

const LoadMoreButton: React.FC<{
  onClick: () => void
  isFetching: boolean
}> = ({ onClick, isFetching }) => {
  const wrapperRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    const containerEl = wrapperRef.current
    if (!containerEl || isFetching) return
    const fetchNextPage = debounce(onClick, 300)
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) fetchNextPage()
    })
    observer.observe(containerEl)
    return () => observer.disconnect()
  }, [wrapperRef, onClick, isFetching])

  return (
    <div ref={wrapperRef}>
      <Button
        color='secondary_outline'
        size='sm'
        onClick={onClick}
        isFullWidth
        isLoading={isFetching}
      >
        Load More
      </Button>
    </div>
  )
}
